import { all, call, put, takeEvery } from 'redux-saga/effects'
import { push } from 'react-router-redux'
import _ from 'lodash'
import { store as reduxStore } from 'index'
import {
  list as extensionsList,
  changeExtensionStatus as extensionChangeStatus,
  isStatusSuccess,
  statusMessage,
} from 'services/extensions'
import type {
  Extension,
  ExtensionCost,
  ExtensionListResponse,
  ExtensionStatusFallback,
  ExtensionStatusResponse,
} from 'services/extensions/types'
import { manualOutreach } from 'services/user'
import { isAuthErrorCode } from 'helpers'
import constants from '../../constants'
import type { App, RootState } from '../types'
import actions from './actions'

// Extensions slice, ported from the `apps/*` handlers, which stay in place for other consumers.
// Deliberate differences are listed in docs/chat-features-migration/PARITY-AUDIT.md §8.

const getRoot = (): RootState => reduxStore.getState() as unknown as RootState

type Saga<P> = { payload?: P }

/** The action shapes this slice's workers accept — the contract every `dispatch` must match.
 *  The generator return stays loose; redux-saga's effect typing fights a precise one. */
type ExtensionsActionPayload = ExtensionsListPayload | EnableDisablePayload | UpgradeIntentPayload

type SagaWorker = (action: { type: string; payload?: ExtensionsActionPayload }) => any

interface ExtensionConstant {
  /** A formatted string in `constants`, e.g. `'1,000.00'` — not a number. */
  cost: string
  v1?: unknown
}

const extensionConstants = (constants as unknown as {
  extensions: Record<string, ExtensionConstant | undefined>
  ManualOutreachIntents: Record<string, string>
})

const extensionCost = (id: string): ExtensionCost => {
  const entry = extensionConstants.extensions[id]
  return entry !== undefined ? entry.cost : 0
}

/** Legacy-region apps only expose extensions that carry a `v1` entry in `constants.extensions`. */
const isAllowedOnLegacyRegion = (id: string): boolean =>
  extensionConstants.extensions[id]?.v1 !== undefined

interface ExtensionsListPayload {
  data?: App
}

/** Loads the region catalog and the app's installed list; both are required, because only
 *  the installed list carries a usable `adminURL`. */
export function* EXTENSIONS_LIST({ payload = {} }: Saga<ExtensionsListPayload> = {}): any {
  yield put({
    type: actions.SET_STATE,
    payload: { areExtensionsLoading: true },
  })

  const activeApp = payload.data || getRoot().apps.active

  try {
    const allExtensions: ExtensionListResponse = yield call(extensionsList, activeApp)

    if (allExtensions && allExtensions.data) {
      const installedExtensions: ExtensionListResponse = yield call(
        extensionsList,
        activeApp,
        'installed',
      )

      let installedExtensionsList: Extension[] = []
      const installedExtensionsIDs: string[] = []

      if (installedExtensions && installedExtensions.data) {
        installedExtensions.data.forEach(ext => {
          ext.price = extensionCost(ext.id)
          ext.installed = true
          installedExtensionsIDs.push(ext.id)
        })
        installedExtensionsList = installedExtensions.data
      }

      if (!_.isEmpty(allExtensions.data)) {
        if (activeApp.region === 'legacy') {
          _.remove(allExtensions.data, ext => !isAllowedOnLegacyRegion(ext.id))
        }
        allExtensions.data.forEach(ext => {
          ext.price = extensionCost(ext.id)
          ext.installed = installedExtensionsIDs.includes(ext.id)
        })
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          extensionsAllList: allExtensions.data,
          extensionsInstalledList: installedExtensionsList,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          extensionsAllList: [],
          extensionsInstalledList: [],
        },
      })
      yield call(handleAuthError, allExtensions?.error)
    }
  } catch {
    window.triggerNotification?.('Could not load extensions. Check your connection.', 'error')
  } finally {
    yield put({
      type: actions.SET_STATE,
      payload: { areExtensionsLoading: false },
    })
  }
}

/** Shared auth-error branch — ported from the live handlers. */
export function* handleAuthError(error?: { code?: string }): any {
  if (error?.code !== undefined && isAuthErrorCode(error.code)) {
    if (error.code === 'AUTH_ERR_TOKEN_BLACKLISTED') {
      yield put({ type: 'user/LOGOUT' })
    } else {
      yield put(push('/login'))
    }
    return true
  }
  return false
}

interface EnableDisablePayload {
  id: string
  callback?: (result: { success: boolean; data?: unknown; error?: unknown }) => void
}

/** Toggles an extension, deriving its current state from `extensionsInstalledList`. Checks the
 *  per-id `success` flag, because the API refuses toggles with HTTP 200 (bug B1). */
export function* ENABLE_DISABLE_EXTENSION({
  payload,
}: Saga<EnableDisablePayload> & { payload: EnableDisablePayload }): any {
  const { id, callback } = payload

  yield put({
    type: actions.SET_STATE,
    payload: { areExtensionsLoading: true },
  })

  const activeApp: App = getRoot().apps.active
  const installedExtensions = getRoot().extensions.extensionsInstalledList

  const current = installedExtensions.find(ext => ext.id === id)
  const isCurrentlyEnabled = Boolean(current?.enabled)

  // The service reads `type` as the extension's CURRENT state: 'enabled' → disable it.
  const requestType = isCurrentlyEnabled ? 'enabled' : 'disabled'

  try {
    const response: ExtensionStatusResponse | ExtensionStatusFallback = yield call(
      extensionChangeStatus as (...args: any[]) => any,
      id,
      activeApp,
      requestType,
    )

    if (isStatusSuccess(response, id)) {
      const refreshed: ExtensionListResponse = yield call(extensionsList, activeApp, 'installed')
      const rows = refreshed?.data ?? []
      rows.forEach(ext => {
        ext.price = extensionCost(ext.id)
        ext.installed = true
      })

      const extensionName = rows.find(ext => ext.id === id)?.name ?? 'Extension'
      const verb = isCurrentlyEnabled ? 'disabled' : 'enabled'

      yield put({
        type: actions.SET_STATE,
        payload: {
          extensionsInstalledList: rows,
          areExtensionsLoading: false,
        },
      })

      window.triggerNotification?.(`${extensionName} has been ${verb}.`, 'success')

      if (callback) callback({ success: true, data: (response as ExtensionStatusResponse).data })
      return
    }

    yield put({
      type: actions.SET_STATE,
      payload: { areExtensionsLoading: false },
    })

    const error = (response as ExtensionStatusResponse)?.error
    const handled = yield call(handleAuthError, error)

    if (!handled) {
      // Prefer the API's per-id reason; fall back to the envelope, then to a generic message.
      const reason =
        statusMessage(response, id) ?? error?.details?.[0] ?? error?.message ?? 'Failed to update extension.'
      window.triggerNotification?.(reason, 'error')
    }

    if (callback) callback({ success: false, error })
  } catch (error) {
    // Backstop only — the service resolves a fallback rather than throwing.
    yield put({
      type: actions.SET_STATE,
      payload: { areExtensionsLoading: false },
    })
    window.triggerNotification?.('Failed to update extension.', 'error')
    if (callback) callback({ success: false, error })
  }
}

interface UpgradeIntentPayload {
  featureSlug?: string
}

/** Ported as-is from `apps/UPGRADE_PLAN_ON_EXTENSION_RESTRICTION_MANUAL_INTENT`. */
export function* UPGRADE_PLAN_ON_EXTENSION_RESTRICTION_MANUAL_INTENT({
  payload,
}: Saga<UpgradeIntentPayload> & { payload: UpgradeIntentPayload }): any {
  const activeApp: App = getRoot().apps.active

  if (_.isEmpty(activeApp) || activeApp.id === undefined) return undefined

  const featureSlug = payload.featureSlug ?? ''
  const intent = _.join(
    [
      extensionConstants.ManualOutreachIntents['add-higher-plan-extension'],
      activeApp.id,
      _.replace(featureSlug, '_', ' '),
    ],
    ' / ',
  )

  try {
    const success = yield call(manualOutreach, { intent })

    const sent = getRoot().extensions.sentManualIntentExtensionIds
    if (!sent.includes(featureSlug)) {
      yield put({
        type: actions.SET_STATE,
        payload: { sentManualIntentExtensionIds: [...sent, featureSlug] },
      })
    }

    return success
  } catch {
    // Fire-and-forget outreach; a failure must not surface to the user or block the toggle.
    return undefined
  }
}

export default function* extensionsSaga(): any {
  yield all([
    takeEvery(actions.EXTENSIONS_LIST, EXTENSIONS_LIST as SagaWorker),
    takeEvery(actions.ENABLE_DISABLE_EXTENSION, ENABLE_DISABLE_EXTENSION as SagaWorker),
    takeEvery(
      actions.UPGRADE_PLAN_ON_EXTENSION_RESTRICTION_MANUAL_INTENT,
      UPGRADE_PLAN_ON_EXTENSION_RESTRICTION_MANUAL_INTENT as SagaWorker,
    ),
  ])
}
